import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoIcon from '@mui/icons-material/Info';
import { Box, Button, Typography } from '@mui/material';

import { MultiSelectFields, RadioTiles } from '@shared-forms-web';
import { reasonsForVisitListData } from '@src/components/ReasonForVisit/ReasonForVisit.helpers';
import { Appointment } from '@src/components/UserPortal/UserPortal.types';

function formatConflictDateTime(isoString: string): string {
	const date = new Date(isoString);
	const monthAbbr = date.toLocaleDateString('en-US', { month: 'short' });
	const day = date.getDate();
	const year = date.getFullYear();
	const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
	return `${monthAbbr}. ${day}, ${year} • ${timeStr}`;
}

function AlreadyBookedBanner({ appointment, testId }: Readonly<{ appointment: Appointment; testId: string }>) {
	return (
		<Box
			data-test-id={testId}
			display="flex"
			alignItems="center"
			gap={1}
			sx={{
				bgcolor: '#002855',
				px: 1.25,
				py: '10px',
				borderRadius: '8px 8px 0 0',
				overflow: 'hidden',
			}}
		>
			<CalendarTodayIcon sx={{ color: 'common.white', fontSize: 20, flexShrink: 0 }} />
			<Typography noWrap sx={{ color: 'common.white', fontSize: 12, fontWeight: 700, lineHeight: '16px' }}>
				Already booked - {formatConflictDateTime(appointment.start)}
			</Typography>
		</Box>
	);
}

export default function ReasonForVisitForm({
	revealSicknessOptions,
	handleOpenModal,
	sickConflict,
	checkupConflict,
}: Readonly<{
	revealSicknessOptions: boolean;
	handleOpenModal: () => void;
	sickConflict?: Appointment | null;
	checkupConflict?: Appointment | null;
}>) {
	return (
		<>
			<RadioTiles
				name="visitReason"
				groupLabel="What brings you in?"
				options={[
					{
						value: 'sick',
						label: 'Sick or Urgent',
						subLabel: 'Sickness, Injury, Vomiting, Lethargic, Not Eating or Drinking',
						headerContent: sickConflict ? (
							<AlreadyBookedBanner appointment={sickConflict} testId="sick-conflict-banner" />
						) : undefined,
					},
					{
						value: 'checkup',
						label: 'Routine Check Up',
						subLabel:
							'Wellness Exams, Vaccinations, Lab Work, Preventative Care, Pre-Dental and Pre-Surgical Exams',
						headerContent: checkupConflict ? (
							<AlreadyBookedBanner appointment={checkupConflict} testId="checkup-conflict-banner" />
						) : undefined,
					},
				]}
				lovetFontAdjustment
			/>
			{revealSicknessOptions && (
				<Box>
					<Typography variant="header3" data-test-id="specific-reason-header">
						Select the reasons below (can be multiple):
					</Typography>
					<Button
						startIcon={<InfoIcon />}
						variant="text"
						sx={{ margin: '0.5rem 0rem 1.25rem' }}
						onClick={handleOpenModal}
						data-test-id="illness-explanation-button"
					>
						What do these mean?
					</Button>
					<Box display="flex" flexWrap="wrap" width="100%" data-test-id="reasons">
						<MultiSelectFields
							name="specificReasonForVisit"
							groupLabel=""
							multiSelectOptions={reasonsForVisitListData.map(({ title }) => ({
								label: title,
								value: title,
							}))}
							allowNone={false}
							required={false}
						/>
					</Box>
				</Box>
			)}
		</>
	);
}
